import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowRight, Cpu, Image, Video } from 'lucide-react';
import { SEO } from '@/components/seo';
import { buttonStyles } from '@/components/ui';

const API_URL = import.meta.env.VITE_API_URL || 'https://lionfish-app-zwr6q.ondigitalocean.app/api/v1';

interface Model {
  id: string;
  slug: string;
  name: string;
  vendor: string;
  description: string | null;
  type: string | null;
  avgDetectionRate: number | null;
  logoUrl: string | null;
}

export function ModelsHub() {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/pages/models`)
      .then(res => res.json())
      .then(data => setModels(data.models || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const getTypeIcon = (type: string | null) => {
    switch (type) {
      case 'video': return <Video className="w-5 h-5" />;
      case 'image': return <Image className="w-5 h-5" />;
      default: return <Cpu className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string | null) => {
    switch (type) {
      case 'video': return 'bg-blue-100 text-blue-600';
      case 'image': return 'bg-purple-100 text-purple-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  // Group by type
  const imageModels = models.filter(m => m.type === 'image');
  const videoModels = models.filter(m => m.type === 'video');
  const otherModels = models.filter(m => !m.type || (m.type !== 'image' && m.type !== 'video'));

  return (
    <>
      <SEO
        title="AI Image Models - Midjourney, DALL-E, Stable Diffusion & More"
        description="Compare AI image generation models. Learn which watermarks each model uses and how to remove them from generated images."
        canonical="/models"
      />

      {/* Hero */}
      <section className="section bg-gradient-to-b from-blue-50 via-white to-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              AI Generation Models
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Explore different AI models and learn how to remove watermarks from images
              generated by each one.
            </p>
            <Link to="/" className={buttonStyles({ size: 'lg' })}>
              Remove Watermarks Free
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Models Grid */}
      <section className="section bg-white">
        <div className="container">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : (
            <div className="space-y-12">
              {/* Image Models */}
              {imageModels.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-100 text-purple-600">
                      <Image className="w-5 h-5" />
                    </span>
                    Image Generation Models
                    <span className="text-sm font-normal text-gray-400">
                      ({imageModels.length})
                    </span>
                  </h2>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {imageModels.map(model => (
                      <Link
                        key={model.id}
                        to={`/models/${model.slug}`}
                        className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-lg transition-all"
                      >
                        <div className="flex items-start justify-between mb-4">
                          {model.logoUrl ? (
                            <img src={model.logoUrl} alt={model.name} className="w-10 h-10 object-contain" />
                          ) : (
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(model.type)}`}>
                              {getTypeIcon(model.type)}
                            </div>
                          )}
                          {model.avgDetectionRate !== null && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              model.avgDetectionRate >= 70 ? 'bg-red-100 text-red-700' :
                              model.avgDetectionRate >= 40 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {Math.round(model.avgDetectionRate)}% detected
                            </span>
                          )}
                        </div>

                        <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                          {model.name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-3">{model.vendor}</p>
                        {model.description && (
                          <p className="text-gray-600 text-sm line-clamp-2">
                            {model.description}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Video Models */}
              {videoModels.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100 text-blue-600">
                      <Video className="w-5 h-5" />
                    </span>
                    Video Generation Models
                    <span className="text-sm font-normal text-gray-400">
                      ({videoModels.length})
                    </span>
                  </h2>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {videoModels.map(model => (
                      <Link
                        key={model.id}
                        to={`/models/${model.slug}`}
                        className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-lg transition-all"
                      >
                        <div className="flex items-start justify-between mb-4">
                          {model.logoUrl ? (
                            <img src={model.logoUrl} alt={model.name} className="w-10 h-10 object-contain" />
                          ) : (
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(model.type)}`}>
                              {getTypeIcon(model.type)}
                            </div>
                          )}
                        </div>

                        <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                          {model.name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-3">{model.vendor}</p>
                        {model.description && (
                          <p className="text-gray-600 text-sm line-clamp-2">
                            {model.description}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Other Models */}
              {otherModels.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100 text-gray-600">
                      <Cpu className="w-5 h-5" />
                    </span>
                    Other Models
                    <span className="text-sm font-normal text-gray-400">
                      ({otherModels.length})
                    </span>
                  </h2>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {otherModels.map(model => (
                      <Link
                        key={model.id}
                        to={`/models/${model.slug}`}
                        className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-lg transition-all"
                      >
                        <div className="flex items-start justify-between mb-4">
                          {model.logoUrl ? (
                            <img src={model.logoUrl} alt={model.name} className="w-10 h-10 object-contain" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 text-gray-600">
                              <Cpu className="w-5 h-5" />
                            </div>
                          )}
                        </div>

                        <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                          {model.name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-3">{model.vendor}</p>
                        {model.description && (
                          <p className="text-gray-600 text-sm line-clamp-2">
                            {model.description}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-primary-600">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Works With All AI Models
          </h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Remove watermarks from images generated by any AI model instantly.
          </p>
          <Link
            to="/"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-primary-600 hover:bg-gray-100',
            })}
          >
            Try Free Now
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
}

export default ModelsHub;
